/**
 * Overtime Card Komponenti
 * 
 * Fazla mesai talebini kart formatında gösteren komponent.
 * Kullanıcı bilgisi, tarih, süre, açıklama ve aksiyon butonlarını içerir.
 * 
 * Özellikler:
 * - Kullanıcı profil resmi ve bilgileri
 * - Tarih ve saat bilgileri
 * - Süre hesaplama ve gösterimi
 * - Durum badge'i
 * - Onayla/Reddet butonları (pending durumunda)
 * - Detay görüntüleme
 */

import React from 'react';
import { Avatar } from 'antd';
import { UserOutlined } from '@ant-design/icons';
import { OvertimeDto, OvertimeStatus } from '../../../../types/overtime.types';
import OvertimeStatusBadge from './OvertimeStatusBadge';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import 'dayjs/locale/tr';
import { getFileUrl } from '../../../../utils/file.utils';

interface OvertimeCardProps {
  overtime: OvertimeDto;
  onApprove?: (overtimeId: number) => void;  // Onaylama callback'i
  onReject?: (overtimeId: number) => void;   // Reddetme callback'i
  onDetail?: (overtimeId: number) => void;   // Detay görüntüleme callback'i
  showActions?: boolean; // Aksiyon butonları gösterilsin mi? (varsayılan: true)
}

/**
 * Fazla mesai kartı komponenti
 * @param overtime - Overtime kaydı
 * @param onApprove - Onaylama callback'i
 * @param onReject - Reddetme callback'i
 * @param onDetail - Detay görüntüleme callback'i
 * @param showActions - Aksiyon butonları gösterilsin mi?
 */
const OvertimeCard: React.FC<OvertimeCardProps> = ({
  overtime,
  onApprove,
  onReject,
  onDetail,
  showActions = true,
}) => {
  const { t, i18n } = useTranslation();

  /**
   * Tarihi formatlar (gün adı ile birlikte)
   * @param date - Tarih string (YYYY-MM-DD)
   * @returns Formatlanmış tarih (ör: "15 Ocak 2024 (Pazartesi)")
   */
  const formatDate = (date: string) => {
    return dayjs(date)
      .locale(i18n.language)
      .format('DD MMMM YYYY (dddd)');
  };

  /**
   * Süreyi formatlar (dakikadan saat/dakikaya çevirir)
   * @param minutes - Dakika cinsinden süre
   * @returns Formatlanmış süre (ör: "2 saat 30 dakika")
   */
  const formatDuration = (minutes: number) => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    
    if (hours === 0) {
      return `${mins} ${t('overtime.minute', 'dakika')}`;
    }
    
    return mins > 0 
      ? `${hours} ${t('overtime.hour', 'saat')} ${mins} ${t('overtime.minute', 'dakika')}` 
      : `${hours} ${t('overtime.hour', 'saat')}`;
  };

  /**
   * Profil resmini al - Backend'den gelen profilePicture varsa URL'ini döndür
   */
  const getProfilePicture = () => {
    // profilePicture varsa ve boş string değilse URL'ini döndür
    if (overtime.user.profilePicture && overtime.user.profilePicture.trim() !== '') {
      return getFileUrl(overtime.user.profilePicture);
    }
    
    // Gender varsa cinsiyete göre default avatar
    if (overtime.user.gender === 'male') {
      return '/assets/img/man.png';
    }
    if (overtime.user.gender === 'female') {
      return '/assets/img/woman.png';
    }
    
    // Yoksa undefined döndür (Avatar component initials gösterecek)
    return undefined;
  };

  return (
    <div className="card border rounded-lg mb-3 hover:shadow-md transition-shadow">
      <div className="card-body">
        {/* Header: Kullanıcı bilgisi ve durum badge'i */}
        <div className="d-flex justify-content-between align-items-start mb-3">
          <div className="d-flex align-items-center gap-3">
            {/* Profil resmi - Avatar component */}
            <Avatar 
              size={48}
              src={getProfilePicture()}
              icon={!getProfilePicture() ? <UserOutlined /> : undefined}
              style={{
                backgroundColor: !getProfilePicture() ? '#1890ff' : undefined,
                fontSize: '20px',
                flexShrink: 0
              }}
            >
              {/* Resim yoksa ilk harfleri göster */}
              {!getProfilePicture() && `${overtime.user.firstName[0]}${overtime.user.lastName[0]}`}
            </Avatar>
            
            {/* Kullanıcı adı ve departman */}
            <div>
              <h5 className="mb-1 fw-semibold">
                {overtime.user.firstName} {overtime.user.lastName}
              </h5>
              <p className="text-muted mb-0" style={{ fontSize: '0.875rem' }}>
                {overtime.user.primaryDepartment?.name || t('overtime.noDepartment', 'Departman Yok')}
              </p>
            </div>
          </div>
          
          {/* Durum badge'i */}
          <OvertimeStatusBadge status={overtime.status} />
        </div>

        {/* Detay bilgileri */}
        <div className="mb-3">
          {/* Tarih */}
          <div className="d-flex align-items-center gap-2 mb-2">
            <i className="ti ti-calendar text-primary" style={{ fontSize: '1.25rem' }} />
            <span className="text-muted" style={{ fontSize: '0.875rem' }}>
              {t('overtime.date', 'Tarih')}:
            </span>
            <span className="fw-medium">{formatDate(overtime.date)}</span>
          </div>

          {/* Süre */}
          <div className="d-flex align-items-center gap-2 mb-2">
            <i className="ti ti-clock text-primary" style={{ fontSize: '1.25rem' }} />
            <span className="text-muted" style={{ fontSize: '0.875rem' }}>
              {t('overtime.duration', 'Süre')}:
            </span>
            <span className="fw-medium">
              {overtime.overtimeStart} - {overtime.overtimeEnd}
              {' '}
              <span className="text-muted">
                ({formatDuration(overtime.overtimeDuration)})
              </span>
            </span>
          </div>

          {/* Açıklama (varsa) */}
          {overtime.description && (
            <div className="d-flex align-items-start gap-2 mb-2">
              <i className="ti ti-file-text text-primary" style={{ fontSize: '1.25rem' }} />
              <span className="text-muted" style={{ fontSize: '0.875rem' }}>
                {t('overtime.description', 'Açıklama')}:
              </span>
              <span>{overtime.description}</span>
            </div>
          )}

          {/* Talep zamanı */}
          <div className="d-flex align-items-center gap-2 text-muted" style={{ fontSize: '0.875rem' }}>
            <i className="ti ti-clock-hour-4" style={{ fontSize: '1rem' }} />
            <span>{t('overtime.requestTime', 'Talep Zamanı')}:</span>
            <span>
              {dayjs(overtime.createdAt).locale(i18n.language).format('DD.MM.YYYY HH:mm')}
            </span>
          </div>

          {/* Red nedeni (varsa) */}
          {overtime.status === OvertimeStatus.REJECTED && overtime.rejectionReason && (
            <div className="alert alert-danger mt-3 mb-0 py-2" role="alert">
              <div className="d-flex align-items-start gap-2">
                <i className="ti ti-alert-circle" style={{ fontSize: '1.25rem' }} />
                <div>
                  <strong>{t('overtime.rejectionReason', 'Red Nedeni')}:</strong>
                  <p className="mb-0 mt-1">{overtime.rejectionReason}</p>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Aksiyon butonları (pending durumunda ve showActions true ise) */}
        {showActions && overtime.status === OvertimeStatus.PENDING && (
          <div className="d-flex gap-2">
            {onApprove && (
              <button
                onClick={() => onApprove(overtime.id)}
                className="btn btn-success flex-fill d-flex align-items-center justify-content-center gap-2"
              >
                <i className="ti ti-check" />
                {t('overtime.approve', 'Onayla')}
              </button>
            )}
            {onReject && (
              <button
                onClick={() => onReject(overtime.id)}
                className="btn btn-danger flex-fill d-flex align-items-center justify-content-center gap-2"
              >
                <i className="ti ti-x" />
                {t('overtime.reject', 'Reddet')}
              </button>
            )}
          </div>
        )}

        {/* Detay butonu (opsiyonel) */}
        {onDetail && (
          <div className="mt-2">
            <button
              onClick={() => onDetail(overtime.id)}
              className="btn btn-sm btn-outline-primary w-100"
            >
              <i className="ti ti-eye me-1" />
              {t('overtime.viewDetails', 'Detayları Gör')}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default OvertimeCard;


